import { useState, useEffect } from "react";
import { MapPin, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { PostLoadCard, PostLoadCardHeader } from "./PostLoadCard";
import { Badge } from "@/components/ui/badge";
import routeMapImg from "@/assets/route-map.jpg";
import type { Stop } from "@/components/post-load/StopCard";

interface RouteMapCardProps {
  stops: Stop[];
  formMode: "pickup" | "drop" | null;
  totalMiles?: number | null;
}

function getMarkerPosition(index: number, total: number) {
  const xStart = 12;
  const xEnd = 88;
  const yCenter = 45;
  const yVariance = 18;
  const x = total === 1 ? 50 : xStart + (xEnd - xStart) * (index / (total - 1));
  const y = yCenter + (index % 2 === 0 ? -yVariance : yVariance) * (index > 0 ? 0.6 : 0);
  return { x, y };
}

export default function RouteMapCard({ stops, formMode, totalMiles }: RouteMapCardProps) {
  const [manualCollapse, setManualCollapse] = useState(false);

  const autoCollapsed = formMode !== null;
  const isCollapsed = autoCollapsed || manualCollapse;

  useEffect(() => {
    if (formMode === null && stops.length >= 2) {
      setManualCollapse(false);
    }
  }, [formMode, stops.length]);

  // Need at least 2 stops with locations filled in
  const filledStops = stops.filter(s => s.city && s.state);
  if (filledStops.length < 2) return null;

  const firstStop = filledStops[0];
  const lastStop = filledStops[filledStops.length - 1];

  const estHours = totalMiles ? Math.round(totalMiles / 50) : null;
  const estMinutes = estHours ? Math.round((totalMiles! / 50 - estHours) * 60) : null;

  return (
    <PostLoadCard>
      <PostLoadCardHeader
        icon={<MapPin className="h-3.5 w-3.5" style={{ color: "hsl(var(--brand-teal-deep))" }} />}
        title="Route Map"
        demoHintKey="section.routeMap"
      >
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className="text-[10px] px-2 py-0 font-semibold"
            style={{
              borderColor: "hsl(var(--brand-teal-deep))",
              color: "hsl(var(--brand-teal-deep))",
            }}
          >
            Stops: {filledStops.length}
          </Badge>
          <button
            onClick={() => setManualCollapse((prev) => !prev)}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            {isCollapsed ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronUp className="h-4 w-4" />
            )}
          </button>
        </div>
      </PostLoadCardHeader>

      {!isCollapsed && (
        <div className="relative w-full overflow-hidden" style={{ height: 290 }}>
          <img
            src={routeMapImg}
            alt="Route map"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, hsl(215 50% 10% / 0.08) 0%, transparent 25%, transparent 70%, hsl(215 50% 10% / 0.2) 100%)",
            }}
          />

          <svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            style={{ pointerEvents: "none" }}
          >
            <polyline
              points={filledStops
                .map((_, i) => {
                  const pos = getMarkerPosition(i, filledStops.length);
                  return `${pos.x},${pos.y}`;
                })
                .join(" ")}
              fill="none"
              stroke="hsl(170, 85%, 32%)"
              strokeWidth="0.6"
              strokeDasharray="1.5,1"
              strokeLinecap="round"
            />
          </svg>

          {filledStops.map((stop, i) => {
            const pos = getMarkerPosition(i, filledStops.length);
            const isPickup = stop.stop_type === "pickup";
            return (
              <div
                key={stop.id}
                className="absolute flex items-center justify-center rounded-full text-[9px] font-bold text-white shadow-md"
                style={{
                  width: 22,
                  height: 22,
                  left: `${pos.x}%`,
                  top: `${pos.y}%`,
                  transform: "translate(-50%, -50%)",
                  backgroundColor: isPickup
                    ? "hsl(152, 69%, 36%)"
                    : "hsl(0, 72%, 51%)",
                  border: "2px solid hsl(0 0% 100% / 0.9)",
                }}
              >
                {i + 1}
              </div>
            );
          })}

          <div
            className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg backdrop-blur-md text-[10px] font-medium"
            style={{
              backgroundColor: "hsl(0 0% 100% / 0.9)",
              color: "hsl(var(--foreground))",
              boxShadow: "var(--shadow-sm)",
            }}
          >
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ backgroundColor: "hsl(152, 69%, 36%)" }}
            />
            <span className="truncate max-w-[100px]">
              {firstStop.city ? `${firstStop.city}, ${firstStop.state}` : "Stop 1"}
            </span>
            <span className="text-muted-foreground">→</span>
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ backgroundColor: "hsl(0, 72%, 51%)" }}
            />
            <span className="truncate max-w-[100px]">
              {lastStop.city ? `${lastStop.city}, ${lastStop.state}` : `Stop ${filledStops.length}`}
            </span>
          </div>

          <div
            className="absolute bottom-10 left-3 right-3 flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-[11px] font-semibold"
            style={{
              backgroundColor: "hsl(var(--brand-teal-deep))",
              color: "hsl(0 0% 100%)",
            }}
          >
            {totalMiles
              ? `${totalMiles.toLocaleString()} mi${estHours ? ` · ${estHours}h ${estMinutes ?? 0}m` : ""}`
              : "-- mi · --h --m"}
          </div>

          <div
            className="absolute bottom-2 left-3 right-3 flex items-center gap-2 px-3 py-1.5 text-[10px] font-medium rounded-lg backdrop-blur-md"
            style={{
              backgroundColor: "hsl(0 0% 100% / 0.88)",
              color: "hsl(var(--brand-teal-deep))",
              boxShadow: "var(--shadow-sm)",
            }}
          >
            <Sparkles className="h-3 w-3 flex-shrink-0" />
            AI: Route optimized for distance and major freight corridors.
          </div>
        </div>
      )}
    </PostLoadCard>
  );
}
